import type { City } from '@/hooks/useDB/city/types/city'
import { db, getCitiesStore, getCitiesByType, searchCitiesByName, getCityDataVersion, saveAllCitiesToDB } from '@/hooks/useDB/city/util/cityDB'
import type { CityRecord } from '@/hooks/useDB/city/util/cityDB'
import { isTestEnv } from '@/config'

export interface FlatListItem extends CityRecord {
  id: number
  size: number
}

export interface CityGroupData {
  indexList: string[]
  groupedData: Record<string, CityRecord[]>
  flatList: FlatListItem[]
}

/** 按首字母分组并构建索引（pageRatio 由 UI 层传入） */
export const groupCityData = (cities: CityRecord[], pageRatio = 1): CityGroupData => {
  const groupedData: Record<string, CityRecord[]> = {}
  const indexList: string[] = []

  const sortedCities = [...cities].sort((a, b) => {
    const letterA = a.firstLetter || a.spell.charAt(0).toUpperCase()
    const letterB = b.firstLetter || b.spell.charAt(0).toUpperCase()
    if (letterA !== letterB) return letterA.localeCompare(letterB)
    return a.spell.localeCompare(b.spell)
  })

  sortedCities.forEach((city) => {
    const letter = city.firstLetter || city.spell.charAt(0).toUpperCase()
    if (!groupedData[letter]) {
      groupedData[letter] = []
      indexList.push(letter)
    }
    groupedData[letter].push(city)
  })

  const flatList: FlatListItem[] = []
  let uid = 0
  indexList.forEach((letter) => {
    flatList.push({
      id: uid++,
      size: 30 * pageRatio,
      code: `__HEADER__${letter}`,
      name: letter,
      spell: letter,
      firstLetter: letter,
      cityLatitude: 0,
      cityLongitude: 0,
      type: -1,
    })
    groupedData[letter].forEach((city) => {
      flatList.push({ ...city, id: uid++, size: 45 * pageRatio })
    })
  })

  return { indexList, groupedData, flatList }
}

// ─── 初始化逻辑（原 BaseDataService.ensureInit 的等价实现）───

let dbOpenPromise: Promise<void> | null = null

/** 确保数据库已打开（不加载数据，仅 open） */
const ensureDBOpen = (): Promise<void> => {
  if (db.isOpen) return Promise.resolve()
  if (dbOpenPromise) return dbOpenPromise
  dbOpenPromise = db.open().catch((e) => {
    dbOpenPromise = null // 失败时清除，允许重试
    throw e
  })
  return dbOpenPromise
}

let initPromise: Promise<void> | null = null

/** 确保数据库已初始化（打开 + 无数据时加载本地 JSON 兜底） */
const ensureInit = async (): Promise<void> => {
  if (initPromise) return initPromise

  initPromise = (async () => {
    try {
      await ensureDBOpen()

      // 检查是否有数据，无数据时加载本地 JSON 兜底
      const store = getCitiesStore()
      const count = await store.count()

      if (count === 0) {
        console.log('[cityService] 本地无版本数据，加载本地 JSON 兜底')
        const host = isTestEnv ? '' : `${location.origin}/static`
        const res = await fetch(`${host}/cityData.json`)
        const raw = await res.json()
        const version = String(raw.lastestChangeTime || Date.now())
        const result = await saveAllCitiesToDB(raw, version)
        if (result === 'updated') {
          window.dispatchEvent(new Event('cityData:updated'))
        }
      }
    } catch (e) {
      initPromise = null // 失败时清除，允许重试
      throw e
    }
  })()

  return initPromise
}

// ─── 读取兜底 ───

/**
 * 确保 DB 中有可用数据。
 * 覆盖场景：页面运行中 DB 被外部删除（如 DevTools 清空 IndexedDB），
 * 此时模块级状态（initPromise）是脏的，需重置后触发本地 JSON 兜底。
 * @returns true 表示数据可用（含恢复成功），false 表示恢复失败
 */
const ensureDataAvailable = async (): Promise<boolean> => {
  const store = getCitiesStore()
  const count = await store.count()
  if (count > 0) return true

  console.warn('[cityService] 检测到 DB 数据为空，重置状态并重新初始化')
  initPromise = null
  try {
    await ensureInit()
    const newCount = await store.count()
    console.log('[cityService] 重新初始化后 DB 数据量:', newCount)
    return newCount > 0
  } catch (e) {
    console.warn('[cityService] 重新初始化失败', e)
    return false
  }
}

// ─── 对外接口（保持与旧版签名一致，调用方无需修改）───

/** 确保 IndexedDB 已初始化 */
export const ensureCityDBInit = (): Promise<void> => ensureInit()

/** 获取 DB 当前版本号（不触发 init，不依赖 DB 打开） */
export const getCityVersion = (): string | undefined => {
  return getCityDataVersion()
}

/** 拉取远程 OSS URL 城市数据（失败返回 null，不自动回退本地） */
export const fetchRemoteCityData = async (url: string): Promise<any | null> => {
  try {
    const res = await fetch(url)
    return await res.json()
  } catch (e) {
    console.error('[cityService] 远程数据拉取失败:', e)
    return null
  }
}

/**
 * 将远程城市数据写入 IndexedDB（raw 为原始 JSON，version 为服务端版本号）
 * @returns 写入结果：'updated' 写入成功 | 'skipped' 版本一致跳过
 */
export const saveCityDataToDB = async (raw: { hotAreas?: City[]; openAreas?: City[] }, version: string): Promise<'updated' | 'skipped'> => {
  await ensureDBOpen()
  const result = await saveAllCitiesToDB(raw, version)
  if (result === 'updated') {
    window.dispatchEvent(new Event('cityData:updated'))
  }
  return result
}

/** 加载热门城市数据（type=0） */
export const loadHotCities = async (): Promise<CityRecord[]> => {
  await ensureDBOpen()
  const cities = await getCitiesByType(0)
  if (cities.length === 0) {
    console.log('[cityService] 本地无热门城市数据，加载本地 JSON 兜底')
    await ensureDataAvailable()
    return await getCitiesByType(0)
  }
  return cities
}

/** 获取已开通城市分组数据（用于城市列表展示） */
export const getOpenCityGroups = async (pageRatio: number): Promise<CityGroupData> => {
  await ensureDBOpen()
  let cities = await getCitiesByType(1)
  if (cities.length === 0) {
    console.log('[cityService] 本地无已开通城市数据，加载本地 JSON 兜底')
    await ensureDataAvailable()
    cities = await getCitiesByType(1)
  }
  return groupCityData(cities, pageRatio)
}

/** 从 IndexedDB 重新加载并分组（收到更新事件后调用） */
export const reloadCityData = async (pageRatio: number): Promise<CityGroupData> => {
  const cities = await getCitiesByType(1)
  return groupCityData(cities, pageRatio)
}

/** 根据城市名称模糊查询已开通城市 */
export const searchCityByName = async (keyword: string): Promise<CityRecord[]> => {
  await ensureDBOpen()
  await ensureDataAvailable()
  return await searchCitiesByName(keyword)
}
